import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { EMPTY, Observable, expand, map, reduce } from 'rxjs';
import { NUTRITION_SERVICE_FE_CONFIG } from './nutrition-service-fe.config';
import type {
  Recipe,
  CreateRecipeRequest,
  UpdateRecipeRequest,
  Category,
  Tag,
  ImportedRecipeIngredient,
  ImportedRecipe,
  ImportedRecipeStatus,
  ImportedRecipeReason,
  UpdateImportedRecipeStatusRequest,
} from '../interfaces/recipe.dto';

/** Backend response wrapper for single recipe */
interface RecipeResponse {
  recipe: Recipe;
  correlationId: string;
}

/** Backend response wrapper for recipe list */
interface RecipesResponse {
  recipes: Recipe[];
  total: number;
  correlationId: string;
}

/** Backend response wrapper for categories */
interface CategoriesResponse {
  categories: Category[];
  correlationId: string;
}

/** Backend response wrapper for tags */
interface TagsResponse {
  tags: Tag[];
  correlationId: string;
}

/** Backend response wrapper for imported recipe ingredients */
interface ImportedRecipeIngredientsResponse {
  ingredients: ImportedRecipeIngredient[];
  total: number;
  correlationId: string;
}

/** Backend response wrapper for imported recipes */
interface ImportedRecipesResponse {
  recipes: ImportedRecipe[];
  total: number;
  correlationId: string;
}

/** Backend response wrapper for single imported recipe */
interface ImportedRecipeResponse {
  recipe: ImportedRecipe;
  correlationId: string;
}

/**
 * Recipe storage service.
 * Communicates with recipe-bucket backend service.
 */
@Injectable({
  providedIn: 'root',
})
export class RecipeBucketService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(NUTRITION_SERVICE_FE_CONFIG, {
    optional: true,
  });

  private getEndpoint(): string {
    return this.config?.recipeBucketEndpoint ?? '/recipe-bucket';
  }

  /**
   * Get all recipes.
   */
  getRecipes(): Observable<{ recipes: Recipe[]; total: number }> {
    const pageSize = 100;
    const endpoint = `${this.getEndpoint()}/recipes`;

    const fetchPage = (offset: number) =>
      this.http.get<RecipesResponse>(endpoint, {
        params: {
          limit: String(pageSize),
          offset: String(offset),
        },
      });

    return fetchPage(0).pipe(
      expand((page, pageIndex) => {
        const nextOffset = (pageIndex + 1) * pageSize;
        return nextOffset < page.total ? fetchPage(nextOffset) : EMPTY;
      }),
      reduce(
        (acc, page) => ({
          recipes: [...acc.recipes, ...page.recipes],
          total: page.total,
        }),
        { recipes: [] as Recipe[], total: 0 },
      ),
    );
  }

  /**
   * Get a single recipe by ID.
   */
  getRecipe(id: string): Observable<Recipe> {
    return this.http
      .get<RecipeResponse>(
        `${this.getEndpoint()}/recipes/${encodeURIComponent(id)}`,
      )
      .pipe(map((r) => r.recipe));
  }

  /**
   * Create a new recipe.
   */
  createRecipe(recipe: CreateRecipeRequest): Observable<Recipe> {
    return this.http
      .post<RecipeResponse>(`${this.getEndpoint()}/recipes`, recipe)
      .pipe(map((r) => r.recipe));
  }

  /**
   * Update an existing recipe.
   */
  updateRecipe(recipe: UpdateRecipeRequest): Observable<Recipe> {
    return this.http
      .put<RecipeResponse>(
        `${this.getEndpoint()}/recipes/${encodeURIComponent(recipe.id)}`,
        recipe,
      )
      .pipe(map((r) => r.recipe));
  }

  /**
   * Delete a recipe.
   */
  deleteRecipe(id: string): Observable<void> {
    return this.http.delete<void>(
      `${this.getEndpoint()}/recipes/${encodeURIComponent(id)}`,
    );
  }

  /**
   * Get all categories.
   */
  getCategories(): Observable<Category[]> {
    return this.http
      .get<CategoriesResponse>(`${this.getEndpoint()}/categories`)
      .pipe(map((r) => r.categories));
  }

  /**
   * Get tags for a specific category.
   */
  getTagsByCategory(categoryId: number): Observable<Tag[]> {
    return this.http
      .get<TagsResponse>(`${this.getEndpoint()}/categories/${categoryId}/tags`)
      .pipe(map((r) => r.tags));
  }

  /**
   * Get imported recipe ingredients with pagination.
   */
  getImportedRecipeIngredients(
    params: { limit?: number; offset?: number } = {},
    token: string,
  ): Observable<{ ingredients: ImportedRecipeIngredient[]; total: number }> {
    const queryParams = new URLSearchParams();
    if (params.limit !== undefined)
      queryParams.set('limit', params.limit.toString());
    if (params.offset !== undefined)
      queryParams.set('offset', params.offset.toString());
    const queryString = queryParams.toString();
    const url = `${this.getEndpoint()}/imported-recipe-ingredients${queryString ? `?${queryString}` : ''}`;

    return this.http
      .get<ImportedRecipeIngredientsResponse>(url, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .pipe(map(({ ingredients, total }) => ({ ingredients, total })));
  }

  /**
   * Get imported recipes with pagination.
   */
  getImportedRecipes(
    params: {
      status?: ImportedRecipeStatus | ImportedRecipeStatus[];
      title?: string;
      reason?: ImportedRecipeReason | ImportedRecipeReason[];
      limit?: number;
      offset?: number;
    } = {},
    token: string,
  ): Observable<{ recipes: ImportedRecipe[]; total: number }> {
    const queryParams = new URLSearchParams();
    if (params.status) {
      const statuses = Array.isArray(params.status)
        ? params.status
        : [params.status];
      statuses.forEach((s) => queryParams.append('status', s));
    }
    if (params.title) {
      queryParams.set('title', params.title);
    }
    if (params.reason) {
      const reasons = Array.isArray(params.reason)
        ? params.reason
        : [params.reason];
      reasons
        .filter((r) => r !== null)
        .forEach((r) => queryParams.append('reason', r!));
    }
    if (params.limit !== undefined)
      queryParams.set('limit', params.limit.toString());
    if (params.offset !== undefined)
      queryParams.set('offset', params.offset.toString());
    const queryString = queryParams.toString();
    const url = `${this.getEndpoint()}/imported-recipes${queryString ? `?${queryString}` : ''}`;

    return this.http
      .get<ImportedRecipesResponse>(url, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .pipe(map(({ recipes, total }) => ({ recipes, total })));
  }

  /**
   * Get single imported recipe by ID with ingredients.
   */
  getImportedRecipeById(id: string, token: string): Observable<ImportedRecipe> {
    const url = `${this.getEndpoint()}/imported-recipes/${id}`;

    return this.http
      .get<ImportedRecipeResponse>(url, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .pipe(map(({ recipe }) => recipe));
  }

  /**
   * Update imported recipe status.
   */
  updateImportedRecipeStatus(
    id: string,
    data: UpdateImportedRecipeStatusRequest,
    token: string,
  ): Observable<ImportedRecipe> {
    const url = `${this.getEndpoint()}/imported-recipes/${id}/status`;

    return this.http
      .patch<ImportedRecipeResponse>(url, data, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .pipe(map(({ recipe }) => recipe));
  }
}
